import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { faPause, faPlay } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import DisableEnableModal from './DisableEnableModal';
import { useAuth } from '../../../context/Auth';

export default function Services() {
    const { styles, theme } = useStyles(styleSheet);
    const isFocused = useIsFocused();
    const { user: authenticatedUser } = useAuth();

    const [lprServiceRunning, setLprServiceRunning] = useState<boolean>(false);
    const [locationServiceRunning, setLocationServiceRunning] = useState<boolean>(true);
    const [locationModalVisible, setLocalModalVisible] = useState<boolean>(false);
    const [lprModalVisible, setLprModalVisible] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    function getServicesStatus() {
        if (window.AndroidBridge?.replyServiceStatus) {
            window.AndroidBridge.replyServiceStatus();
        }
    }

    function getPageContent() {
        setIsLoading(true);
        window.getServicesStatus = (locationServiceRunning: string, lprServiceRunning: string) => {
            setLprServiceRunning(lprServiceRunning == 'true');
            setLocationServiceRunning(locationServiceRunning == 'true');
            setIsLoading(false);

        };
        getServicesStatus();
    }

    useFocusEffect(useCallback(() => {
        if (isFocused || !lprModalVisible || !locationModalVisible) {
            getPageContent();
        }
    }, [isFocused, window, lprModalVisible, locationModalVisible]));

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <View style={styles.container}>
            <View style={styles.headerContent}>
                <MyAppText style={styles.headerText(22)}>{translate('services')}</MyAppText>
            </View>
            <View style={styles.card}>
                {isLoading ?
                    <ActivityIndicator size='large' style={styles.loadingContainer} color={theme.colors.dataTable.loading} /> :
                    <>
                        <View style={styles.serviceContainer}>
                            <MyAppText style={styles.headerText(16)}>{translate('locationService')}</MyAppText>
                            <CustomButton
                                onPress={() => {
                                    setLocalModalVisible(true);
                                }}
                                disabled={!window.AndroidBridge || !window.AndroidBridge.setLprServiceRunning || !window.AndroidBridge.setLocationServiceRunning || lprServiceRunning}
                                text={translate(locationServiceRunning ? 'disable' : 'enable')}
                                icon={(locationServiceRunning ? faPause : faPlay) as IconDefinition}
                            />
                        </View>
                        {authenticatedUser.permissions.app_agent_manage_services &&
                            <View style={styles.serviceContainer}>
                                <MyAppText style={styles.headerText(16)}>{translate('lprService')}</MyAppText>
                                <CustomButton
                                    onPress={() => {
                                        setLprModalVisible(true);
                                    }}
                                    disabled={!window.AndroidBridge || !window.AndroidBridge.setLprServiceRunning || !window.AndroidBridge.setLocationServiceRunning}
                                    text={translate(lprServiceRunning ? 'disable' : 'enable')}
                                    icon={(lprServiceRunning ? faPause : faPlay) as IconDefinition}
                                />
                            </View>
                        }
                    </>}
            </View>

            <DisableEnableModal
                isModalVisible={locationModalVisible}
                setIsModalVisible={setLocalModalVisible}
                service='LocationService'
                serviceStatus={locationServiceRunning}
            />

            <DisableEnableModal
                isModalVisible={lprModalVisible}
                setIsModalVisible={setLprModalVisible}
                service='LprService'
                serviceStatus={lprServiceRunning}
            />
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        rowGap: 10
    },
    headerContent: {
        flexDirection: 'row',
        minHeight: 40,
        alignItems: 'center',
        padding: 8,
    },
    headerText: (fontSize: number) => {
        return {
            fontSize,
            fontWeight: '600',
            color: theme.colors.neutral['500'],
        };
    },
    card: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.card.border,
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.colors.card.background,
        flex: 1,
        gap: 30
    },
    serviceContainer: {
        gap: 10
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center'
    },
}));
